import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { router } from '@/app/routes';
import { ThemeProvider } from '@/app/providers/ThemeProvider';
import { ErrorBoundary } from '@/app/providers/ErrorBoundary';
import { neustartErlaubt } from '@/app/nachladeFehler';
import { seedIfEmpty } from '@/db/seed';
import { festeBereicheSicherstellen } from '@/db/repositories/areas';
import { syncInitialisieren } from '@/modules/sync/syncDienst';
import { autoSicherungInitialisieren } from '@/modules/settings/autoSicherungDienst';
import { speicherBeimStart } from '@/lib/backup/storage';
import { papierkorbAufraeumen } from '@/db/repositories/papierkorb';
import { invariant } from '@/lib/utils/invariant';
import '@fontsource-variable/manrope';
import '@/styles/tailwind.css';

const root = document.getElementById('root');
invariant(root, 'Wurzelelement #root fehlt in index.html.');

// Beide duerfen fehlschlagen, ohne den Start zu blockieren: ohne dauerhaften
// Speicher laeuft die App weiter (nur riskanter), und ein fehlgeschlagener
// Seed darf den Nutzer nicht aussperren.
// Bei jedem Start pruefen (F-37); die Uebersicht warnt, wenn er nicht dauerhaft ist.
void speicherBeimStart();
void seedIfEmpty().catch((err) => console.error('Erststart-Befüllung fehlgeschlagen:', err));
// Synchronisierung (T-04): nur wenn auf diesem Geraet eingerichtet.
// Danach den Papierkorb aufraeumen (F-45, Eintraege nach 30 Tagen endgueltig
// entfernen) - erst dann ist die Erfassung aktiv und die Loeschungen gehen mit
// in den Abgleich.
void syncInitialisieren()
  .catch((err) => console.error('Synchronisierung nicht gestartet:', err))
  // Feste Bereiche (BE-02) erst nach dem ersten Abgleich: sonst legte ein zweites
  // Geraet "Gesundheit & Körper" an, bevor der vorhandene Bereich ankommt.
  .then(() => festeBereicheSicherstellen())
  .catch((err) => console.error('Feste Bereiche nicht angelegt:', err))
  .then(() => papierkorbAufraeumen())
  .catch((err) => console.error('Papierkorb nicht aufgeräumt:', err));
// Automatische Sicherung in einen Ordner (F-46): nur mit gewaehltem Ordner.
void autoSicherungInitialisieren().catch((err) => console.error('Automatische Sicherung nicht gestartet:', err));

// Im gebauten Modus meldet Vite fehlgeschlagenes Vorladen eigens - nach
// einem Update also einmal neu laden (siehe app/nachladeFehler.ts).
window.addEventListener('vite:preloadError', (event) => {
  if (neustartErlaubt()) {
    event.preventDefault();
    window.location.reload();
  }
});

// Service Worker (F-31): installierbar als App und Push bei geschlossener
// App. Speichert nichts zwischen; ohne Unterstuetzung laeuft alles weiter.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('Service Worker nicht registriert:', err));
}

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>,
);
